/**
 * Аппын фрэйм — прототипийн `01 Dashboard.dc.html`-ээс хөрвүүлсэн.
 *
 * БҮТЭЦ (чухал): бүх дэлгэцийг эзэлнэ, сайдбар ба header нь ТОГТМОЛ.
 * Зөвхөн голын агуулга гүйлгэгдэнэ.
 *
 *   h-dvh overflow-hidden
 *   ├─ aside  (shrink-0, доторх цэс нь өөрөө гүйлгэгдэнэ)
 *   └─ div    (flex-1, min-w-0)
 *      ├─ header      shrink-0
 *      └─ main        flex-1 overflow-y-auto   ← ЗӨВХӨН энэ гүйнэ
 *
 * ⚠️ Прототипийн «1С синк» тэмдэглэгээг ХУУЛААГҮЙ — энэ систем 1С-тэй
 *    ярьдаггүй. Оронд нь Оросын sync-ийн бодит төлөвийг харуулна.
 */

import { formatQty, formatRub, formatSize } from '@boots/domain';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ROLE_LABEL, type BranchDashboard, type Dashboard } from '../lib/types';
import { useTheme } from '../lib/theme';
import { sizeFromSearch } from '../lib/use-items-filter';
import { AssistantButton } from './assistant-panel';
import { HostingBalancePill } from './hosting-balance';

interface NavCounts {
  purchases?: number;
  items?: number;
  orders?: number;
  stores?: number;
  staff?: number;
}

export interface Alert {
  title: string;
  meta: string;
  urgent: boolean;
}

interface NavItem {
  label: string;
  to?: string;
  end?: boolean;
  badge?: number;
  /**
   * Энэ мөрийг харахад шаардлагатай эрх — `"модуль.үйлдэл"`.
   * Заагаагүй бол БҮГД харна.
   *
   * ⚠️ Утга нь ГАРААР сонгосон зүйл биш: тухайн хуудсын каталог дахь
   *    `view` эрх (`@boots/domain` → `PERM_MODULES`). Админ түүнийг
   *    унтраавал цэс ЭНД, API ТЭНД хоёулаа хаагдана — нэг тохиргоо.
   *
   * ⚠️ Цэс нуух нь ХАМГААЛАЛТ БИШ, зөвхөн эмх цэгц. Route нь App.tsx-д
   *    хэвээр байгаа ба шууд URL-ээр орвол сервер 403 өгнө.
   *
   * Жагсаалт өгвөл АЛЬ НЭГ нь хангалттай — `/` нь компанийн ба салбарын
   * хоёр самбарын аль нэгийг эрхээр сонгодог (`App.tsx` → `HomePage`).
   */
  perm?: string | string[];
}

/**
 * Цэс — БИЗНЕСИЙН ХЭСГҮҮДЭЭР бүлэглэнэ.
 *
 * Өмнө нь «ӨДӨР ТУТАМ / ГУТЛЫН УРСГАЛ / БИЗНЕСИЙН ТОЙМ / ТОХИРГОО»
 * гэж ХЭРЭГЛЭХ ДАВТАМЖААР бүлэглэсэн байв. Ажилтан өөрийгөө
 * «агуулахын хүн», «салбарын хүн» гэж боддог болохоос «өдөр тутмын
 * хүн» гэж боддоггүй — тиймээс бүлэг нь ажлын байрны хэсгүүдийг дагана.
 *
 * ⚠️ Хяналтын самбар БҮЛЭГГҮЙ (`title: ''`) — үндсэн хуудас, аль ч
 *    хэсэгт харьяалагдахгүй. Хоосон гарчиг нь `NavGroup`-д гарахгүй.
 *
 * ⚠️ ДАРААЛАЛ: самбарын дараа СТАТИСТИК (өдөр бүр эхэлж хардаг),
 *    төгсгөлд нь СИСТЕМИЙН ТОХИРГОО (ховор ордог). Энэ дараалал нь
 *    `@boots/domain`-ийн `PERM_GROUP_LABEL`-тэй ИЖИЛ байх ёстой —
 *    админ эрх тохируулахдаа энэ цэсээ хайдаг.
 *
 * ⚠️ «Салбар» нэр хоёр газар давхарлаж байсан: бүлгийн гарчиг ба
 *    `/branches` хуудас. Хуудсыг «Салбарын тойм» гэв — бүртгэлийн
 *    хуудас нь «Салбар, агуулах» (Системийн тохиргоо) хэвээр.
 */
function navGroups(n: NavCounts): { title: string; items: NavItem[] }[] {
  return [
    {
      title: '',
      items: [
        {
          label: 'Хяналтын самбар',
          to: '/',
          end: true,
          perm: ['dashboard.view', 'branchDashboard.view'],
        },
      ],
    },
    {
      title: 'СТАТИСТИК',
      items: [
        { label: 'Статистик', to: '/stats', perm: 'dashboard.view' },
        { label: 'Хуанли', to: '/calendar', perm: 'calendar.view' },
        { label: 'Тайлан', to: '/reports', perm: 'reports.view' },
        { label: 'Санхүү, нэхэмжлэх', to: '/finance', perm: 'finance.view' },
      ],
    },
    {
      // Нягтлан бодогч (2026-10-15) — 5 таб нэг хуудсанд (`/accounting?tab=`)
      title: 'НЯГТЛАН',
      items: [{ label: 'Нягтлангийн тайлан', to: '/accounting', perm: 'accounting.view' }],
    },
    {
      /**
       * ⚠️ АГУУЛАХ багцаас ГАРГАСАН. «Бүх гутал» нь агуулахын дотоод
       *    ажил БИШ: энэ хуудас системд байгаа БҮХ гутлыг — зарагдсан,
       *    буцаагдсан, актлагдсаныг ч — харуулдаг тул салбарын
       *    ажилтан, менежер ч ижилхэн ордог. Агуулахын доор байхад
       *    «энэ надад хамаагүй» гэж уншигдана.
       */
      title: 'ГУТАЛ',
      items: [
        { label: 'Бүх гутал', to: '/items', badge: n.items, perm: 'items.view' },
      ],
    },
    {
      title: 'НИЙЛҮҮЛЭЛТ',
      items: [
        {
          label: 'Аялал - Орос',
          to: '/purchasing',
          badge: n.purchases,
          perm: 'purchasing.view',
        },
        { label: 'Зээл, төлөлт', to: '/credit', perm: 'credit.view' },
        { label: 'Нийлүүлэгч', to: '/setup/suppliers', perm: 'setupSuppliers.view' },
      ],
    },
    {
      title: 'АГУУЛАХ',
      items: [
        { label: 'Агуулах', to: '/warehouse', perm: 'items.view' },
        { label: 'Шилжүүлэг', to: '/transfers', perm: 'transfers.view' },
        { label: 'Тооллого', to: '/stock-counts', perm: 'stockCounts.view' },
      ],
    },
    {
      title: 'САЛБАР',
      items: [
        { label: 'Касс', to: '/sales', perm: 'sales.view' },
        { label: 'Захиалга, борлуулалт', to: '/orders', badge: n.orders, perm: 'orders.view' },
        // Менежер «Шилжүүлэг» хуудсыг харахгүй — салбарын хүлээн авалт энд
        { label: 'Хүлээн авалт', to: '/receiving', perm: 'transfers.receive' },
        { label: 'Хувь зээл', to: '/installments', perm: 'installments.view' },
        { label: 'Баталгаа', to: '/warranty', perm: 'warranty.view' },
        { label: 'Буцаалт, солилцоо', to: '/returns', perm: 'orders.refund' },
        { label: 'Салбарын тойм', to: '/branches', badge: n.stores, perm: 'branches.view' },
      ],
    },
    {
      title: 'ЦАХИМ ХУДАЛДАА',
      items: [
        { label: 'Бөөний худалдаа', to: '/wholesale', perm: 'wholesale.view' },
      ],
    },
    {
      title: 'СИСТЕМИЙН ТОХИРГОО',
      items: [
        { label: 'Салбар, агуулах', to: '/setup/stores', perm: 'setupStores.view' },
        { label: 'Ажилтан', to: '/setup/staff', badge: n.staff, perm: 'setupStaff.view' },
        { label: 'Жагсаалт, ангилал', to: '/setup/reference', perm: 'setupReference.view' },
        { label: 'Систем', to: '/setup/system', perm: 'setupSystem.view' },
      ],
    },
  ];
}

/**
 * ⚠️ «Агуулах» (`/warehouse`) нь ЗӨВХӨН биет нэгж — `stores.type =
 *    WAREHOUSE`. `/items` нь системд байгаа БҮХ гутлын шүүгддэг
 *    жагсаалт тул «Бүх гутал». Хоёр өөр зүйл, нэрийг
 *    хэзээ ч сольж болохгүй.
 */
const TITLES: Record<string, string> = {
  '/': 'Хяналтын самбар',
  '/items': 'Бүх гутал',
  '/warehouse': 'Агуулах',
  '/branches': 'Салбарын тойм',
  '/purchasing': 'Аялал - Орос',
  '/credit': 'Нийлүүлэгчийн зээл',
  '/installments': 'Хувь зээл',
  '/orders': 'Захиалга, борлуулалт',
  '/sales': 'Касс — борлуулалт бүртгэх',
  '/transfers': 'Шилжүүлэг',
  '/receiving': 'Хүлээн авалт — агуулахаас ирсэн бараа',
  '/stock-counts': 'Тооллого',
  '/warranty': 'Баталгаа',
  '/returns': 'Буцаалт, солилцоо',
  '/finance': 'Санхүү, нэхэмжлэх',
  '/wholesale': 'Бөөний худалдаа',
  '/stats': 'Статистик',
  '/reports': 'Тайлан',
  '/calendar': 'Хуанли',
  '/setup/suppliers': 'Нийлүүлэгч',
  '/setup/stores': 'Салбар, агуулах',
  '/setup/staff': 'Ажилтан — бүртгэл',
  '/setup/reference': 'Жагсаалт, ангилал',
  '/setup/system': 'Системийн тохиргоо',
};

function pageTitle(pathname: string): string {
  if (pathname.startsWith('/items/')) return 'Гутлын дэлгэрэнгүй';
  if (pathname.startsWith('/purchasing/')) return 'Аялалын дэлгэрэнгүй';
  return TITLES[pathname] ?? 'Цаатан';
}

/** Дашбоардын тооноос анхааруулга гаргана — хиймэл мэдэгдэл үүсгэхгүй. */
export function alertsFrom(d: Dashboard | undefined): Alert[] {
  if (!d) return [];
  const out: Alert[] = [];

  if (d.counts.zeroPriced > 0) {
    out.push({
      title: `${formatQty(d.counts.zeroPriced)} гутал зарагдах байршилд 0₮ үнэтэй байна`,
      meta: 'Агуулах · үнэ тавигдаагүй',
      urgent: true,
    });
  }
  if (d.counts.unlabeled > 0) {
    out.push({
      title: `${formatQty(d.counts.unlabeled)} гутал шошгогүй`,
      meta: 'Агуулах · серийн дугаар хүлээж буй',
      urgent: false,
    });
  }
  if (d.counts.unsynced > 0) {
    out.push({
      title: `${formatQty(d.counts.unsynced)} бичлэг sync хийгдээгүй`,
      meta: 'Орос · багц ирээгүй',
      urgent: true,
    });
  }
  /**
   * Зээлийн хугацаа хэтэрсэн.
   *
   * ⚠️ Энэ нь «мэдэгдэл»-ийн ГАЗАР: push мэдэгдэл байхгүй (Firebase
   *    хориотой — CLAUDE.md §Технологи), тиймээс админ, захирал вэб
   *    нээхэд энд харагдана. Цуглуулагч Оросод хугацаагаа зааж
   *    өгснөөр л энэ анхааруулга үүсэх боломжтой болдог.
   */
  if (d.credit && d.credit.overdue > 0) {
    out.push({
      title: `${formatQty(d.credit.overdue)} аялалын зээлийн хугацаа хэтэрсэн`,
      meta: `Нийлүүлэгч · ${formatRub(d.credit.overdueRub)} төлөгдөөгүй`,
      urgent: true,
    });
  }
  return out;
}

/**
 * Салбарын ажилтны мэдэгдэл — өөрийн салбарын самбараас.
 *
 * ⚠️ «Дууссан / цөөн размер»-ийг ЭНД шийдэхгүй — сервер `level`-ийг
 *    `@boots/domain` → `sizeStockRows`-оор тавьж ирүүлнэ. Самбарын
 *    размерын хавтан ч тэр талбарыг уншдаг тул хоёр зөрөхгүй.
 */
export function branchAlertsFrom(b: BranchDashboard | undefined): Alert[] {
  if (!b?.store) return [];
  const at = b.store.name;
  const sizesAt = (level: 'out' | 'low') =>
    b.sizes.filter((s) => s.level === level).map((s) => formatSize(s.size)).join(', ');
  const out: Alert[] = [];

  const gone = sizesAt('out');
  if (gone) out.push({ title: `${gone} размер дууссан`, meta: `${at} · агуулахаас татуулна уу`, urgent: true });

  const low = sizesAt('low');
  if (low) out.push({ title: `${low} размер цөөн үлдсэн`, meta: `${at} · нөөц багасч байна`, urgent: false });

  if (b.stock.zeroPriced > 0) {
    out.push({
      title: `${formatQty(b.stock.zeroPriced)} гутал лангуун дээр 0₮ үнэтэй`,
      meta: `${at} · касс дээр уншигдахгүй`,
      urgent: true,
    });
  }

  const transit = b.inTransit.reduce((n, t) => n + t.count, 0);
  if (transit > 0) {
    out.push({
      title: `${formatQty(transit)} гутал замд ирж яваа`,
      meta: `${at} · ирмэгц уншуулж хүлээн авна`,
      urgent: false,
    });
  }
  return out;
}

/** Цэс бэхлэгдсэн эсэх — төхөөрөмжид үлдэнэ (`'0'` = хураасан). */
const NAV_KEY = 'nav-docked';

export function AppShell({ counts, alerts }: { counts: NavCounts; alerts: Alert[] }) {
  const { user, readOnly, hasPerm, can } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLElement>(null);
  const [mobileNav, setMobileNav] = useState(false);
  /**
   * Цэс том дэлгэцэнд бэхлэгдсэн эсэх.
   *
   * Суурин касс (D3 Pro) дээр цэс нь 224px иддэг — кассчин өдөржин
   * нэг хуудсан дээр байдаг тул хураагаад сагсанд зай өгнө. Сонголт
   * нь төхөөрөмжид үлдэнэ: кассчин өглөө бүр дахин хураах ёсгүй.
   */
  const [docked, setDocked] = useState(() => {
    try {
      return localStorage.getItem(NAV_KEY) !== '0';
    } catch {
      // Private горим — тухайн сесс дотор бэхлэгдсэн байдлаар явна
      return true;
    }
  });

  const toggleDocked = () => {
    setDocked((prev) => {
      try {
        localStorage.setItem(NAV_KEY, prev ? '0' : '1');
      } catch {
        // Хадгалж чадаагүй ч тухайн сесс дотор ажиллана
      }
      // Хураахад хөвөгч цэс нээлттэй үлдвэл агуулга далд хэвээр байна
      setMobileNav(false);
      return !prev;
    });
  };

  // Хуудас солигдоход агуулгыг эхэнд нь буцаана. Гүйлгэх нь энэ
  // контейнерт байгаа тул window.scrollTo ажиллахгүй.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    setMobileNav(false);
  }, [pathname]);

  // Escape-ээр цэс хаагдана — NotificationBell-тэй ижил зан.
  useEffect(() => {
    if (!mobileNav) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileNav(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNav]);

  const initials = (user?.fullName ?? '')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('');

  // Дүрд хамаарахгүй хуудсыг цэснээс хасна. Бүлэг бүхэлдээ хоосон
  // болбол ГАРЧГИЙГ НЬ Ч гаргахгүй — хоосон гарчиг нь «энд ямар нэг
  // юм байх ёстой» гэсэн буруу дохио өгнө.
  const groups = navGroups(counts)
    .map((g) => ({ ...g, items: g.items.filter((n) => !n.perm || [n.perm].flat().some(hasPerm)) }))
    .filter((g) => g.items.length > 0);

  const [titleMain, titleSub] = pageTitle(pathname).split(' — ');

  return (
    // `isolate` — арын гэрэл (`-z-10`) энэ эхийн дэвсгэрийн ДЭЭР, агуулгын ДООР
    <div className="relative isolate flex h-dvh overflow-hidden bg-bg">
      {/* Харанхуй горимын арын гэрэл (dark glass) — гэрэлд `display: none` */}
      <div aria-hidden className="ambient -z-10">
        <span />
        <span />
        <span />
      </div>

      {/* ─────────── Сайдбар — тогтмол ─────────── */}
      {/* Бэхлэгдсэн үед `rail` (харанхуйд тунгалаг), хөвөх үед дүүрэн `panel` */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-sidebar shrink-0 flex-col border-r
          border-hair bg-panel transition-transform ${
            docked ? 'lg:visible lg:static lg:translate-x-0 lg:bg-rail' : ''
          } ${mobileNav ? 'visible translate-x-0 shadow-pop' : 'invisible -translate-x-full'}`}
      >
        <div className="flex shrink-0 items-center gap-2.5 px-card pt-card pb-gutter">
          {/* Тэмдэг дэвсгэргүй — сайдбарын цагаан дээр шууд суух */}
          <img src="/logo.svg" alt="" className="brand-mark w-9 shrink-0" width={36} height={28} />
          <span className="flex min-w-0 flex-col gap-px">
            <span className="text-[15px] font-semibold tracking-[0.06em]">ЦААТАН</span>
            <span className="text-[11px] text-ink-2">Годон гутлын их дэлгүүр</span>
          </span>
          {/* Том дэлгэцэнд л утгатай — гар утсанд бүрхэвч дээр дарж хаана */}
          <button
            type="button"
            onClick={() => toggleDocked()}
            aria-label={docked ? 'Цэс хураах' : 'Цэс бэхлэх'}
            title={docked ? 'Цэс хураах' : 'Цэс бэхлэх'}
            className="ml-auto hidden size-9 shrink-0 place-items-center rounded-full text-ink-2
              transition hover:bg-well lg:grid"
          >
            {docked ? '⟨' : '⟩'}
          </button>
        </div>

        {/* Цэс урт болбол өөрөө гүйнэ — хэрэглэгчийн карт доор наалдана.
            `scroll-slim` (index.css) — анхдагч 17px гүйлгүүр сайдбарын
            өргөнийг иддэг, нэр нэг мөрт багтахгүй болдог. */}
        <nav className="scroll-slim flex flex-1 flex-col gap-card overflow-y-auto px-3 pb-2">
          {groups.map((g) => (
            <div key={g.title} className="flex flex-col gap-[3px]">
              {/* Хяналтын самбар бүлэггүй — хоосон гарчиг зай эзлэхгүй */}
              {g.title ? (
                <span className="truncate px-2.5 pb-1.5 text-[10.5px] tracking-[0.14em] text-ink-3">
                  {g.title}
                </span>
              ) : null}
              {g.items.map((n) => <NavRow key={n.label} item={n} />)}
            </div>
          ))}
        </nav>

        <div className="m-3 mt-0 flex shrink-0 items-center gap-2.5 rounded-well bg-well p-3">
          {/* Зураг нь Тохиргоо → Ажилтан дээр байршуулагдана; байхгүй
              бол нэрний үсэг (зураг ачаалагдаагүй үед ч хоосон дугуй
              харагдахгүй). */}
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="size-[30px] shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-accent text-xs text-white">
              {initials || '?'}
            </span>
          )}
          <span className="flex min-w-0 flex-col gap-px">
            {/* Нэг мөр — бүтэн нэр `title`-д үлдэнэ. Хоёр мөр болбол
                картны өндөр хэрэглэгч бүрт өөр болж, цэсний доод
                хязгаар хөвж эхэлдэг байв. */}
            <span className="truncate text-[13px]" title={user?.fullName}>
              {user?.fullName}
            </span>
            <span className="truncate text-[11px] text-ink-2">
              {user ? ROLE_LABEL[user.role] : ''}
            </span>
          </span>
        </div>
      </aside>

      {/* Гар утсанд сайдбар нээгдэхэд арын бүрхэвч */}
      {mobileNav ? (
        <button
          type="button"
          aria-label="Цэс хаах"
          onClick={() => setMobileNav(false)}
          className={`fixed inset-0 z-30 bg-scrim/40 ${docked ? 'lg:hidden' : ''}`}
        />
      ) : null}

      {/* ─────────── Гол багана ─────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header — тогтмол */}
        {/*
          320px дээр гарчиг + баруун талын хяналтууд НЭГ мөрөнд багтдаггүй
          (~349px шаардана, 288px байдаг) тул «Гарах» товч тайрагдаж байв.
          `flex-wrap` + гарчиг `order-last w-full` → гар утсанд гарчиг хоёр
          дахь мөрөнд бүтэн өргөнөөр буух ба sm:-ээс дээш нэг мөр хэвээр.
        */}
        <header
          className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-chrome-line
            bg-chrome px-4 py-[11px] sm:flex-nowrap sm:gap-4 sm:px-gutter sm:py-3"
        >
          {/* Харанхуйд gradient лого хавтан (`fill-primary`), гэрэлд энгийн товч */}
          <button
            type="button"
            onClick={() => setMobileNav(true)}
            aria-label="Цэс нээх"
            className={`fill-primary -ml-2 grid size-11 shrink-0 place-items-center rounded-chip text-ink-2
              transition hover:bg-well active:scale-[0.94] sm:ml-0 sm:size-10 ${docked ? 'lg:hidden' : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden fill="none">
              <path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>

          {/* Кирилл гарчиг тайрагдахгүй — багтахгүй бол боогдоно.
              «Касс — борлуулалт бүртгэх» → тайлбар хэсэг нь бүдэг. */}
          <h1
            className="order-last w-full min-w-0 text-[15px] font-bold text-pretty
              sm:order-none sm:w-auto sm:flex-1 sm:text-lg"
          >
            {titleMain}
            {titleSub ? <span className="font-medium text-ink-3"> — {titleSub}</span> : null}
          </h1>

          {/* «38» → 38 размер, бусад нь «Бүх гутал»-ын хайлт */}
          <HeaderSearch
            onSubmit={(q) => {
              const size = sizeFromSearch(q);
              navigate(size ? `/items?size=${size}` : `/items?q=${encodeURIComponent(q)}`);
            }}
          />

          <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
            {readOnly ? (
              <span className="hidden rounded-full bg-accent-100 px-3 py-1.5 text-[12.5px] text-accent-800 sm:inline">
                Зөвхөн харах
              </span>
            ) : null}

            {/* Прототипийн «1С синк» → Оросын sync-ийн бодит төлөв */}
            <SyncPill unsynced={alerts.some((a) => a.meta.startsWith('Орос'))} />

            {/* Vultr кредит — зөвхөн ADMIN */}
            {can('ADMIN') ? <HostingBalancePill /> : null}

            {/* AI туслах — системийн тухай асуух, тайлан Excel-ээр авах */}
            <AssistantButton />

            <NotificationBell alerts={alerts} />

            {/*
              Бүтэн дэлгэц, өнгөний горим, гарах — НЭГ цэсэнд. Гурвуулаа
              өдөрт нэг л удаа хэрэглэгддэг зүйл тул толгой мөрөнд
              байнга зогсох шаардлагагүй.
            */}
            <HeaderMenu />
          </div>
        </header>

        {/* ─────────── ЗӨВХӨН ЭНЭ ГҮЙНЭ ─────────── */}
        {/* Агуулга боломжит бүх өргөнийг эзэлнэ — max-width хязгаар тавихгүй */}
        <main ref={scrollRef} className="flex-1 overflow-y-auto overscroll-contain">
          <div className="min-h-full w-full px-4 pt-4 pb-8 sm:px-gutter">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function HeaderSearch({ onSubmit }: { onSubmit: (q: string) => void }) {
  const [q, setQ] = useState('');

  function submit(e: FormEvent) {
    e.preventDefault();
    const v = q.trim();
    if (v) onSubmit(v);
  }

  return (
    <form onSubmit={submit} className="hidden min-w-0 xl:block">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-[340px] max-w-full rounded-full border border-hair bg-field px-[18px] py-2.5
          text-base text-ink transition placeholder:text-ink-3 focus:border-accent focus:ring-4
          focus:ring-accent-100 focus:outline-none sm:text-sm"
        placeholder="Серийн дугаар эсвэл размер (38)…"
        aria-label="Хайх"
        type="search"
      />
    </form>
  );
}

function NotificationBell({ alerts }: { alerts: Alert[] }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // Гадуур дарах / Escape дарахад хаагдана — эс тэгвээс цонх гацна
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={alerts.length ? `${alerts.length} мэдэгдэл` : 'Мэдэгдэл алга'}
        aria-label={alerts.length ? `${alerts.length} мэдэгдэл` : 'Мэдэгдэл алга'}
        /*
          ⚠️ ЗӨВХӨН ИКОН. Өмнө нь «● 3 мэдэгдэл ▼» гэсэн өргөн товч байв —
             хажуугийн бүтэн дэлгэц, горим, гарах товчтой нийлээд толгой
             мөр нь тасралтгүй эгнээ болж, хуудсын гарчгийг шахдаг байсан.
             Тоо нь дүрсний булан дээрх тэмдэгт болов.
        */
        className={`relative grid size-11 place-items-center rounded-pill border transition
          active:translate-y-[0.5px] sm:size-9 ${
            alerts.length
              ? 'border-alert-bg bg-alert-bg text-alert-ink hover:bg-alert-bg-2'
              : 'border-hair bg-panel text-ink-2 hover:bg-well hover:text-ink'
          }`}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            {/* Хонх — доор нь хэл */}
            <path d="M3.4 11.4h9.2l-1.1-1.7V7.2a3.5 3.5 0 0 0-7 0v2.5z" />
            <path d="M6.6 11.4a1.4 1.4 0 0 0 2.8 0" />
          </g>
        </svg>
        {alerts.length ? (
          <span
            className="num absolute -top-1 -right-1 min-w-[17px] rounded-full bg-alert-dot
              px-1 text-[10px] leading-[17px] text-white"
          >
            {alerts.length > 9 ? '9+' : alerts.length}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Мэдэгдэл"
          className="absolute top-[38px] right-0 z-50 flex w-[min(352px,calc(100vw-2rem))] flex-col
            gap-2.5 rounded-pop border border-hair bg-panel px-4 py-3.5 shadow-pop"
        >
          <div className="flex items-baseline">
            <span className="text-sm font-semibold">Мэдэгдэл</span>
            <span className="ml-auto text-[11.5px] text-alert-ink">{alerts.length} шинэ</span>
          </div>
          {alerts.length === 0 ? (
            <p className="py-3 text-center text-[13px] text-ink-2">Анхаарах зүйл алга</p>
          ) : (
            alerts.map((a) => (
              <div
                key={a.title}
                className={`flex items-start gap-[11px] rounded-2xl px-3 py-2.5 ${
                  a.urgent ? 'bg-alert-bg' : 'bg-notice-bg'
                }`}
              >
                <span
                  className={`mt-1 size-2 shrink-0 rounded-full ${a.urgent ? 'bg-alert-dot' : 'bg-accent'}`}
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-[13px] leading-[1.35]">{a.title}</span>
                  <span className="text-[11px] text-ink-2">{a.meta}</span>
                </div>
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function NavRow({ item }: { item: NavItem }) {
  const badge = item.badge === undefined ? '' : formatQty(item.badge);

  // Хийгдээгүй хэсэг — 404 руу залгахаас илүү тодорхой
  if (!item.to) {
    return (
      <div
        className="flex min-h-11 cursor-not-allowed items-center gap-2.5 rounded-lg px-[11px]
          py-[7px] opacity-45 lg:min-h-0"
        title="Хараахан хийгдээгүй"
      >
        <span className="size-[7px] shrink-0 rounded-full bg-transparent" />
        <span className="min-w-0 truncate text-[13.5px] text-ink-2">{item.label}</span>
        <span className="num ml-auto shrink-0 text-[11.5px] text-ink-3">{badge}</span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `flex min-h-11 items-center gap-2.5 rounded-lg px-[11px] py-[7px] transition lg:min-h-0 ${
          isActive ? 'bg-accent-100 text-accent-700' : 'text-ink-2 hover:bg-well'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`size-[7px] shrink-0 rounded-full ${isActive ? 'bg-accent' : 'bg-transparent'}`} />
          {/* НЭГ МӨР. Сайдбар 264px — одоогийн бүх нэр багтана; ирээдүйд
              урт нэр нэмэгдвэл «…» болно, `title`-д бүтнээрээ үлдэнэ. */}
          <span className="min-w-0 truncate text-[13.5px]" title={item.label}>{item.label}</span>
          <span className={`num ml-auto shrink-0 text-[11.5px] ${isActive ? 'text-accent-700' : 'text-ink-3'}`}>
            {badge}
          </span>
        </>
      )}
    </NavLink>
  );
}

function SyncPill({ unsynced }: { unsynced: boolean }) {
  return (
    <span
      className={`hidden items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold md:flex ${
        unsynced
          ? 'border-alert/30 bg-alert-bg text-alert-ink'
          : 'border-accent-300 bg-accent-100 text-accent-700'
      }`}
      title={unsynced ? 'Оросоос ирээгүй бичлэг бий' : 'Бүх багц хүлээн авсан'}
    >
      {/* Тэлэх цагираг — `ping` нь currentColor-оор зурна */}
      <span className={`ping size-2 rounded-full ${unsynced ? 'bg-alert-dot text-alert-dot' : 'bg-accent text-accent'}`} />
      Sync · {unsynced ? 'хүлээгдэж буй' : 'бүрэн'}
    </span>
  );
}

/**
 * Толгой мөрийн цэс — бүтэн дэлгэц, өнгөний горим, гарах.
 *
 * ⚠️ Өмнө нь гурвуулаа тусдаа товчоор эгнэж, мэдэгдэл, sync-ийн
 *    товчтой нийлээд толгой мөр нь хуудсын гарчгийг шахаж байв.
 *    Өдөрт нэг удаа дардаг зүйлс цэсэнд ороод, байнга хэрэгтэй
 *    (хайлт, мэдэгдэл) нь гадна үлдэв.
 *
 * ⚠️ Бүтэн дэлгэцийн ТӨЛВИЙГ хөтчөөс уншина (`fullscreenchange`) —
 *    хэрэглэгч F11 эсвэл Esc-ээр гарахад цэсний бичиг хоцрохгүй.
 */
function HeaderMenu() {
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(() => Boolean(document.fullscreenElement));
  const { logout } = useAuth();
  const boxRef = useRef<HTMLDivElement>(null);
  const { resolved, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    const sync = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleFull = () => {
    // Хөтөч татгалзвал чимээгүй үлдэнэ — касс ажиллах чадвараа алдахгүй
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    else void document.documentElement.requestFullscreen().catch(() => {});
    setOpen(false);
  };

  const ITEM =
    'flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-[13px] transition hover:bg-well';

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        title="Цэс"
        aria-label="Цэс"
        className="grid size-11 place-items-center rounded-pill border border-hair bg-panel
          text-ink-2 transition hover:bg-well hover:text-ink active:translate-y-[0.5px] sm:size-9"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <g fill="currentColor">
            <circle cx="3" cy="8" r="1.4" />
            <circle cx="8" cy="8" r="1.4" />
            <circle cx="13" cy="8" r="1.4" />
          </g>
        </svg>
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Цэс"
          className="absolute top-[46px] right-0 z-50 w-56 overflow-hidden rounded-card
            border border-hair bg-panel shadow-pop sm:top-[38px]"
        >
          <button type="button" role="menuitem" className={ITEM} onClick={toggleFull}>
            {full ? '⤡' : '⤢'} {full ? 'Бүтэн дэлгэцээс гарах' : 'Бүтэн дэлгэц'}
          </button>
          <button
            type="button"
            role="menuitem"
            className={ITEM}
            onClick={() => { toggleTheme(); setOpen(false); }}
          >
            {resolved === 'light' ? '☾' : '☀'}{' '}
            {resolved === 'light' ? 'Харанхуй горим' : 'Гэрэл горим'}
          </button>
          <button
            type="button"
            role="menuitem"
            className={`${ITEM} border-t border-hair text-alert-ink`}
            onClick={() => void logout()}
          >
            ⏻ Гарах
          </button>
        </div>
      ) : null}
    </div>
  );
}
